import { useCallback, useEffect, useState } from 'react';
import type { ApiClient, CapabilityQuoteView, CapabilityRunView } from '../api/client';
import { toDisplayError } from '../api/errorMessages';

export const DEFAULT_POLL_INTERVAL_MS = 2500;

/**
 * UI phase for the async capability probe. `running` covers both enqueuing and the
 * queued/running poll; `result` is a `done` verdict; `failed` is a transient infra
 * error (retryable, distinct from an `inconclusive` verdict).
 */
export type CapabilityPhase =
  | { kind: 'idle' }
  | { kind: 'quoting' }
  | { kind: 'consent'; quote: CapabilityQuoteView }
  | { kind: 'running' }
  | { kind: 'result'; run: CapabilityRunView }
  | { kind: 'failed'; run: CapabilityRunView }
  | { kind: 'error'; message: string };

export interface UseCapabilityProbe {
  phase: CapabilityPhase;
  latest: CapabilityRunView | null;
  loadingLatest: boolean;
  /** Begin a check (quote → consent | immediate verdict). No-op unless `canRun`. */
  start: () => void;
  /** Accept the consent dialog: record consent, enqueue, then poll. */
  accept: (expectedMicroUsd: string) => void;
  /** Decline the consent dialog: NO API calls. */
  decline: () => void;
  /** Re-run after a done/failed result. */
  retry: () => void;
}

export function useCapabilityProbe(opts: {
  client: ApiClient;
  orgId: string;
  canRun: boolean;
  pollIntervalMs?: number | undefined;
}): UseCapabilityProbe {
  const { client, orgId, canRun } = opts;
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  const [phase, setPhase] = useState<CapabilityPhase>({ kind: 'idle' });
  const [latest, setLatest] = useState<CapabilityRunView | null>(null);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);

  // Load the latest prior run on mount (read-only; any member).
  useEffect(() => {
    let cancelled = false;
    setLoadingLatest(true);
    void (async () => {
      try {
        const run = await client.getLatestCapabilityRun(orgId);
        if (!cancelled) setLatest(run);
      } catch {
        // Non-fatal: fall through to the empty state.
      } finally {
        if (!cancelled) setLoadingLatest(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, orgId]);

  const settle = useCallback((run: CapabilityRunView) => {
    if (run.state === 'done') {
      setPendingRunId(null);
      setLatest(run);
      setPhase({ kind: 'result', run });
    } else if (run.state === 'failed') {
      setPendingRunId(null);
      setPhase({ kind: 'failed', run });
    } else {
      setPendingRunId(run.runId); // queued | running → poll
      setPhase({ kind: 'running' });
    }
  }, []);

  // Poll a pending run until it settles. Cleanup-flag guards unmount races; a
  // single pendingRunId guarantees no duplicate probe is enqueued.
  useEffect(() => {
    if (!pendingRunId) return;
    let cancelled = false;
    async function poll() {
      try {
        const run = await client.getCapabilityRun(orgId, pendingRunId!);
        if (cancelled || !run) return;
        if (run.state === 'done' || run.state === 'failed') settle(run);
      } catch {
        // Transient poll failure: keep the last state and retry next tick.
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pendingRunId, client, orgId, pollIntervalMs, settle]);

  const start = useCallback(() => {
    if (!canRun) return;
    setPhase({ kind: 'quoting' });
    void (async () => {
      try {
        const quote = await client.getCapabilityQuote(orgId);
        if (!quote.quoteAvailable || !quote.spendRequired) {
          // No price, or a zero-spend verdict (sub-8k / unknown context): enqueue
          // directly — the backend returns an immediate verdict without consent.
          settle(await client.enqueueCapabilityProbe(orgId));
          return;
        }
        setPhase({ kind: 'consent', quote });
      } catch (err) {
        setPhase({ kind: 'error', message: toDisplayError(err).message });
      }
    })();
  }, [canRun, client, orgId, settle]);

  const accept = useCallback(
    (expectedMicroUsd: string) => {
      setPhase({ kind: 'running' });
      void (async () => {
        try {
          const consent = await client.recordCapabilityConsent(orgId);
          // Informed consent: if the price/config drifted since the quote was shown,
          // do NOT spend — ask the customer to review the new estimate.
          if (consent.quotedMicroUsd !== expectedMicroUsd) {
            setPhase({
              kind: 'error',
              message: 'The estimated cost changed. Please review it and try again.',
            });
            return;
          }
          settle(await client.enqueueCapabilityProbe(orgId));
        } catch (err) {
          setPhase({ kind: 'error', message: toDisplayError(err).message });
        }
      })();
    },
    [client, orgId, settle],
  );

  const decline = useCallback(() => {
    setPhase({ kind: 'idle' }); // no API calls
  }, []);

  return { phase, latest, loadingLatest, start, accept, decline, retry: start };
}
